import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { ContractVisitGroup } from '../components/VisitGroupEditor'
import {
  CombinationRule,
  ServiceForGrouping,
  suggestVisitGroupsDayCentric,
} from '../lib/visitGroupSuggestion'

export type { CombinationRule, ServiceForGrouping }

export interface UseCombinationRulesResult {
  rules: CombinationRule[]
  loading: boolean
  refresh: () => Promise<void>
  suggestGroups: (
    services: ServiceForGrouping[],
    contractId: string,
    contractStartDate?: string | null,
    contractEndDate?: string | null
  ) => ContractVisitGroup[]
  suggestGroupsDayCentric: (
    services: ServiceForGrouping[],
    contractId: string,
    contractStartDate?: string | null,
    contractEndDate?: string | null
  ) => ContractVisitGroup[]
}

export function useCombinationRules(): UseCombinationRulesResult {
  const [rules, setRules] = useState<CombinationRule[]>([])
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('service_category_combination_rules')
        .select('category_id, combination_group, is_standalone')
      if (error) throw error
      setRules(data || [])
    } catch (e) {
      console.error('Failed to load combination rules:', e)
      setRules([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    refresh()
  }, [refresh])

  const suggestGroups = useCallback(
    (
      services: ServiceForGrouping[],
      contractId: string,
      contractStartDate?: string | null,
      contractEndDate?: string | null
    ): ContractVisitGroup[] => {
      if (!services || services.length === 0) return []

      const rulesByCategory: Record<
        string,
        { combination_group: string; is_standalone: boolean }
      > = {}
      for (const r of rules) {
        rulesByCategory[r.category_id] = {
          combination_group: r.combination_group,
          is_standalone: r.is_standalone,
        }
      }

      // Bucket key = combination_group + sorted recurrence_days
      // Standalone services or services without a rule get a unique bucket key
      const buckets: Record<
        string,
        {
          groupName: string
          days: number[]
          services: ServiceForGrouping[]
          dsny: boolean
        }
      > = {}
      let standaloneIdx = 0

      for (const s of services) {
        const catId = (s as any).service_type_id || (s as any).category_id || ''
        const rule = catId ? rulesByCategory[catId] : undefined
        const days = (s.recurrence_days || []).slice().sort((a: number, b: number) => a - b)
        const dayKey = days.join(',') || 'none'

        let bucketKey: string
        let groupLabel: string

        if (!rule || rule.is_standalone) {
          standaloneIdx++
          bucketKey = `standalone_${standaloneIdx}_${s.id}`
          groupLabel = s.service_name || s.name || 'Service'
        } else {
          bucketKey = `${rule.combination_group}::${dayKey}`
          groupLabel = rule.combination_group
        }

        if (!buckets[bucketKey]) {
          buckets[bucketKey] = {
            groupName: groupLabel,
            days,
            services: [],
            dsny: false,
          }
        }
        buckets[bucketKey].services.push(s)
        if ((s as any).dsny_integration) {
          buckets[bucketKey].dsny = true
        }
      }

      const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
      const out: ContractVisitGroup[] = []
      let idx = 0

      Object.values(buckets).forEach((bucket) => {
        idx++
        const dayLabel =
          bucket.days.length > 0
            ? bucket.days.map((d: number) => DAY_LABELS[d]).join('/')
            : 'Unscheduled'

        const name =
          bucket.services.length === 1
            ? `${bucket.services[0].service_name || bucket.services[0].name || 'Service'} (${dayLabel})`
            : `${bucket.groupName} (${dayLabel})`

        out.push({
          id: `temp-suggest-${Date.now()}-${idx}`,
          contract_id: contractId,
          name,
          recurrence_type: 'weekly',
          recurrence_days: bucket.days,
          start_date: contractStartDate || null,
          end_date: contractEndDate || null,
          scheduled_start_time: null,
          scheduled_end_time: null,
          billing_driver: null,
          dsny_integration: bucket.dsny,
          dsny_pickup_days: [],
          is_active: true,
          service_ids: bucket.services.map((s) => s.id),
        })
      })

      return out
    },
    [rules]
  )

  const suggestGroupsDayCentric = useCallback(
    (
      services: ServiceForGrouping[],
      contractId: string,
      contractStartDate?: string | null,
      contractEndDate?: string | null
    ): ContractVisitGroup[] =>
      suggestVisitGroupsDayCentric(services, rules, contractId, contractStartDate, contractEndDate),
    [rules]
  )

  return { rules, loading, refresh, suggestGroups, suggestGroupsDayCentric }
}
